import { useEffect, useRef, useState } from 'react';
import { INFRA_MONITORING_DASHBOARD_URL } from '../data/infraMonitoringConfig';

// Embeds AWS's own public, read-only CloudWatch dashboard share link via
// iframe - see data/infraMonitoringConfig.js for why this needs no BFF
// route/IAM/fetch at all, unlike every other page in this dashboard.
//
// True content-based auto-resize isn't actually possible here: the iframe
// is cross-origin (cloudwatch.amazonaws.com vs this app's own CloudFront
// domain), so this page can never read the embedded page's real
// document/scrollHeight - the browser's same-origin policy blocks that
// outright, and AWS's public dashboard-sharing feature doesn't document
// posting its own height back via postMessage the way some embeddable
// widgets do. Two things are done instead, in order of preference:
//
//   1. A `message` listener, in case CloudWatch's dashboard.html (or some
//      iframe-resizer-style script running inside it) ever does post a
//      height - not a documented/guaranteed API for this feature, so this
//      is a "use it if it happens to arrive" upgrade, not something to
//      rely on.
//   2. A generous fixed fallback height (see FALLBACK_HEIGHT_PX) sized well
//      above what a typical multi-widget CloudWatch dashboard needs, so the
//      common case has no inner iframe scrollbar at all - the OUTER page
//      scrolls past any unused space instead. This is the actual fix for
//      "no more scrollbar" in practice.
const FALLBACK_HEIGHT_PX = 1200;
const MIN_HEIGHT_PX = 600;

export default function InfraMonitoring() {
  const [loaded, setLoaded] = useState(false);
  const [height, setHeight] = useState(FALLBACK_HEIGHT_PX);
  const iframeRef = useRef(null);

  useEffect(() => {
    function handleMessage(event) {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
        return;
      }
      const data = event.data;
      const candidate =
        typeof data === 'number'
          ? data
          : typeof data?.height === 'number'
          ? data.height
          : typeof data?.frameHeight === 'number'
          ? data.frameHeight
          : null;
      if (candidate && candidate > 0) {
        setHeight(Math.max(MIN_HEIGHT_PX, Math.ceil(candidate) + 24));
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div>
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0 position-relative">
          {!loaded && (
            <div
              className="d-flex align-items-center justify-content-center text-muted"
              style={{ height: '400px' }}
            >
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Loading CloudWatch dashboard…
            </div>
          )}
          <iframe
            ref={iframeRef}
            title="Instapay Prod Infra Monitoring"
            src={INFRA_MONITORING_DASHBOARD_URL}
            onLoad={() => setLoaded(true)}
            style={{
              width: '100%',
              height: `${height}px`,
              border: 'none',
              display: loaded ? 'block' : 'none',
            }}
          />
        </div>
      </div>
    </div>
  );
}
